import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { pluralReadableTypeName, toTitleCase } from '@iotv/core-services';
import { BranchingQueryDefinition, edgeTypeMatrix, Ruleset, RulesetClassName, TimeBucketUnits } from '@iotv/datamodel';
import PublicIcon from '@mui/icons-material/Public';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { shortDefs } from '../../../ArbitrationObjects/ListControlArbitrator/shortDefs';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { ActionIcons } from '../../../Iconography';
import { AdjacentType } from '../../../types/AppTypes';
import { DataGridRadioColumn } from '../../../util/DataGrid/DataGridHelpers';
import { matching } from '../Utilities/CompareRulesets';
import { GlobalIcon } from '../Utilities/GlobalIcon';
import { RulesetCard } from './RulesetCard';
const debug = process.env.REACT_APP_DEBUG && false;
const columnLayout = {
    display: 'flex',
    resizable: false,
    sortable: false,
    disableColumnMenu: true,
};
const rulesetColumns = [
    {
        field: 'global',
        headerName: 'Type',
        ...columnLayout,
        width: 55,
        renderCell: (params) => {
            let TypeIcon = undefined;
            switch (params.value) {
                case true:
                    TypeIcon = _jsx(PublicIcon, { color: "primary" });
                    break;
                case undefined:
                case false:
                    TypeIcon = _jsx(PublicOffIcon, { sx: { opacity: 0.6 } });
                    break;
            }
            return _jsx("div", { style: { flex: 1, display: 'flex', justifyContent: 'center' }, children: TypeIcon });
        },
    },
    {
        field: 'name',
        headerName: 'Name',
        ...columnLayout,
        minWidth: 200,
        sortable: true,
        resizable: true,
    },
    {
        field: 'globallyApplyToObjectTypeId',
        headerName: 'Applies',
        ...columnLayout,
        width: 70,
        valueFormatter: (value) => (value ? 'All' : 'Group(s)'),
    },
    {
        field: 'requestVideoFootage',
        headerName: 'Video',
        ...columnLayout,
        width: 70,
        renderCell: ({ value }) => value === true ? _jsx(Typography, { sx: { fontSize: 'inherit' }, children: "Yes" }) : _jsx(Typography, { sx: { fontSize: 'inherit' }, children: "No" }),
    },
    {
        field: 'appliesToType',
        headerName: 'Entity',
        ...columnLayout,
        valueFormatter: (value) => (value ? pluralReadableTypeName(value) : value),
    },
    {
        field: 'setOperation',
        headerName: 'Trigger',
        ...columnLayout,
        width: 70,
        valueFormatter: (value) => (value === 'AND' ? 'All rules' : 'Any rule'),
    },
    {
        field: 'period',
        headerName: 'Period',
        ...columnLayout,
        width: 65,
    },
    {
        field: 'unit',
        headerName: 'Unit',
        ...columnLayout,
        width: 60,
        valueFormatter: (value) => Object.entries(TimeBucketUnits)
            .filter(([_, unit]) => unit === value)
            .map(([key]) => toTitleCase(key))
            .map((unit) => unit)
            .shift() ?? value,
    },
    {
        field: 'validBetween',
        headerName: 'Valid Between',
        ...columnLayout,
        minWidth: 150,
        valueFormatter: (value) => {
            const fromHour = (value?.from.hour ?? 0).toString().padStart(2, '0');
            const fromMinute = (value?.from.minute ?? 0).toString().padStart(2, '0');
            const toHour = (value?.to.hour ?? 23).toString().padStart(2, '0');
            const toMinute = (value?.to.minute ?? 59).toString().padStart(2, '0');
            return `${fromHour}:${fromMinute} ~ ${toHour}:${toMinute}`;
        },
    },
];
const columns = [DataGridRadioColumn, ...rulesetColumns];
export const RulesetsTab = ({ contextCustomer, userFunctions, user, userGroupRoles, userSelectedGroup, roleArbitrationInstance, isSmallView, }) => {
    const allowedActions = roleArbitrationInstance
        ? roleArbitrationInstance.getObjectControlActionsByType({ sourceObjectType: 'Customer', targetObjectType: RulesetClassName })
        : shortDefs.noAction;
    const singleObjectAllowedActions = roleArbitrationInstance ? roleArbitrationInstance.getSingleObjectControlActions(RulesetClassName) : shortDefs.noAction;
    const [seedCustomer, setSeedCustomer] = useState();
    const [queryingGlobal, setQueryingGlobal] = useState(false);
    const [globalRulesets, setGlobalRulesets] = useState([]);
    // Fetch the seed customer
    useEffect(() => {
        if (seedCustomer?.sk)
            return;
        ConsumerDao.getNamedApexItems({ type: 'Customer' }).then(({ err, data }) => {
            if (err || !data) {
                debug && console.log('RulesetsTab getNamedApexItems', err, data);
            }
            else {
                const c = data.shift();
                if (c)
                    setSeedCustomer(c);
            }
        });
    }, [seedCustomer?.sk]);
    // Fetch global rulesets adjacent to the seed customer (true global)
    useEffect(() => {
        if (!seedCustomer || queryingGlobal)
            return;
        setQueryingGlobal(true);
        ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [seedCustomer],
            query: new BranchingQueryDefinition({ id: seedCustomer.sk, path: [{ objectTypeId: RulesetClassName, adjacencyType: AdjacentType.CHILD }] }),
        }).then(({ err, data }) => {
            if (err || !data) {
                debug && console.log('RulesetsTab getGlobalRulesets', err, data);
                setQueryingGlobal(false);
            }
            else {
                setGlobalRulesets(data
                    .getNodesOfType(RulesetClassName)
                    .map((r) => new Ruleset(r))
                    .filter((r) => r.global)
                    .sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0));
                setQueryingGlobal(false);
            }
        });
    }, [seedCustomer?.sk]);
    const [querying, setQuerying] = useState(false);
    const [rulesets, setRulesets] = useState([]);
    // data grid selection
    const [selectedRuleset, setSelectedRuleset] = useState(undefined);
    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
    useEffect(() => {
        const selectedIds = 'ids' in rowSelectionModel ? Array.from(rowSelectionModel.ids) : rowSelectionModel;
        const found = rulesets.find((r) => r.id === selectedIds[0]);
        // deep copy needed to avoid rule quirkiness
        setSelectedRuleset(found ? structuredClone(found) : undefined);
    }, [rowSelectionModel, rulesets]);
    // data grid selection
    const fetch = async () => {
        if (!contextCustomer)
            return;
        setQuerying(true);
        const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [contextCustomer],
            query: new BranchingQueryDefinition({
                id: contextCustomer.sk,
                path: [
                    {
                        adjacencyType: AdjacentType.CHILD,
                        objectTypeId: RulesetClassName,
                        edgeTypeId: edgeTypeMatrix.Customer.Ruleset.customerHasRuleset.edgeTypeId,
                    },
                ],
                subQueries: [],
            }),
        });
        if (err)
            return;
        const vobs = (data?.getNodesOfType(RulesetClassName) ?? []);
        const instances = vobs.map((item) => new Ruleset(item));
        instances.sort((a, b) => a?.name?.localeCompare(b?.name ?? '') ?? 0);
        setRulesets(instances);
        setQuerying(false);
    };
    useEffect(() => {
        if (contextCustomer)
            fetch();
    }, [contextCustomer?.sk]);
    const handleAdd = () => {
        const newRuleset = { type: RulesetClassName };
        setSelectedRuleset(newRuleset);
    };
    const updateRuleset = async (ruleset) => {
        const index = rulesets.findIndex((ruleset) => ruleset.id === selectedRuleset?.id);
        if (index === -1) {
            if (ruleset) {
                await fetch();
                setRowSelectionModel({ type: 'include', ids: new Set([ruleset.id]) });
            }
            else {
                setSelectedRuleset(undefined);
            }
        }
        else {
            setRulesets((r) => {
                if (ruleset)
                    return r.toSpliced(index, 1, ruleset);
                else
                    return r.toSpliced(index, 1);
            });
        }
    };
    const Icon = ({ ruleset: rulesetIn }) => {
        const ruleset = new Ruleset(rulesetIn);
        // if we are the seed customer, custom logic
        if (seedCustomer?.sk === contextCustomer?.sk) {
            return (_jsx("div", { style: { flex: 1, display: 'flex', justifyContent: 'center', fontSize: '1.5em' }, children: _jsx(GlobalIcon, { global: ruleset, local: ruleset, mismatched: [] }) }));
        }
        const global = globalRulesets.find((r) => r.name === ruleset.name);
        const mismatched = global !== undefined && ruleset !== undefined ? matching(global, ruleset) : [];
        return (_jsx("div", { style: { flex: 1, display: 'flex', justifyContent: 'center', fontSize: '1.5em' }, children: !queryingGlobal ? _jsx(GlobalIcon, { global: global, local: ruleset, mismatched: mismatched }) : _jsx(Skeleton, { variant: "circular", width: "1em", height: "1em" }) }));
    };
    // Handle `Type` column for global ruleset icon indicator
    columns[1].renderCell = (params) => _jsx(Icon, { ruleset: params.row });
    const clonedColumns = [...columns];
    return (_jsxs(Stack, { minHeight: '40cqh', gap: 1, children: [_jsx(DataGrid, { 
                // We must wait for the seed customer to finish otherwise the DataGrid doesn't re-render the global type cells properly
                loading: querying && seedCustomer === undefined, rows: querying && seedCustomer === undefined ? [] : rulesets, columns: clonedColumns, checkboxSelection: true, rowSelectionModel: rowSelectionModel, onRowSelectionModelChange: setRowSelectionModel, disableMultipleRowSelection: true, pageSizeOptions: [10], hideFooter: rulesets.length <= 10, hideFooterPagination: rulesets.length <= 10, initialState: { sorting: { sortModel: [{ field: 'name', sort: 'asc' }] }, pagination: { paginationModel: { pageSize: 10, page: 0 } } } }, 'RulesetsDataGrid'), seedCustomer !== undefined && selectedRuleset !== undefined ? (_jsx(RulesetCard, { seedCustomer: seedCustomer, contextCustomer: contextCustomer, userFunctions: userFunctions, user: user, userGroupRoles: userGroupRoles, userSelectedGroup: userSelectedGroup, viewObject: { matchedPrimary: selectedRuleset, matchedRelatedByPk: undefined, matchedRelatedBySk: undefined }, allowedActions: singleObjectAllowedActions, updateInParent: updateRuleset, notifyDeleted: () => {
                    setSelectedRuleset(undefined);
                    fetch();
                }, isSmallView: isSmallView }, selectedRuleset.sk)) : allowedActions.add ? (_jsx(Stack, { direction: "row", children: _jsx(Button, { variant: "contained", size: "small", startIcon: _jsx(ActionIcons.Create, {}), onClick: handleAdd, children: "Add Ruleset" }) })) : null] }, 'RulesetsTabMainGrid'));
};
